import React, { useState, useEffect } from 'react';
import VoiceAssistantButton from './VoiceAssistantButton';
import VoiceMessageButton from './VoiceMessageButton';
import { Phone, MessageSquare, Mic2, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceInterfaceCombined = ({
  assistantId,
  onCallStart,
  onCallEnd,
  onTranscript,
  onError,
  className = '',
  preferredLanguage = 'fr'
}) => {
  const [showFallback, setShowFallback] = useState(false);
  const [mode, setMode] = useState('webrtc');
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [presets, setPresets] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [activeId, setActiveId] = useState(assistantId);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings/voice-assistant`)
      .then(res => {
        setPresets(res.data.presets);
        setActiveId(res.data.assistant_id);
        setSelectedVoice(res.data.active_assistant);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (assistantId) setActiveId(assistantId);
  }, [assistantId]);

  const handleVoiceSelect = (key, preset) => {
    setSelectedVoice(key);
    setActiveId(preset.id);
    setShowVoiceMenu(false);
    localStorage.setItem('drbooking_preferred_voice', key);
  };

  // Load user preference from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('drbooking_preferred_voice');
    if (saved && presets?.[saved]) {
      setSelectedVoice(saved);
      setActiveId(presets[saved].id);
    }
  }, [presets]);

  const voiceLabel = selectedVoice === 'azure' ? 'Azure' : selectedVoice === 'combine' ? 'Combiné' : 'Standard';

  return (
    <div className={`fixed bottom-28 right-4 z-[9999] ${className}`}>
      {/* Voice selector popup */}
      {showVoiceMenu && presets && (
        <div className="absolute bottom-[110px] right-0 bg-white shadow-2xl rounded-xl border p-3 w-56 animate-in fade-in slide-in-from-bottom-2" data-testid="voice-selector-menu">
          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Voix</p>
          {Object.entries(presets).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => handleVoiceSelect(key, preset)}
              className={`w-full text-left p-2 rounded-lg mb-1 text-sm transition-all ${
                selectedVoice === key
                  ? 'bg-blue-50 border border-blue-200 text-blue-700'
                  : 'hover:bg-slate-50 text-slate-600'
              }`}
              data-testid={`voice-option-${key}`}
            >
              <p className="font-medium">{preset.name}</p>
              <p className="text-xs text-slate-400">{preset.voice}</p>
            </button>
          ))}
        </div>
      )}

      {/* Mode selector + voice selector */}
      <div className="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-white shadow-xl rounded-full p-1 border border-gray-200">
        <button
          onClick={() => setMode('webrtc')}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
            mode === 'webrtc' 
              ? 'bg-blue-500 text-white shadow-md' 
              : 'text-gray-500 hover:bg-gray-100'
          }`}
          title="Appel en direct (WebRTC)"
          data-testid="voice-mode-webrtc"
        >
          <Phone className="w-3 h-3" />
          Direct
        </button>
        <button
          onClick={() => setMode('message')}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
            mode === 'message' 
              ? 'bg-emerald-500 text-white shadow-md' 
              : 'text-gray-500 hover:bg-gray-100'
          }`}
          title="Message vocal (plus fiable)"
          data-testid="voice-mode-message"
        >
          <MessageSquare className="w-3 h-3" />
          Message
        </button>
        {presets && Object.keys(presets).length > 1 && (
          <button
            onClick={() => setShowVoiceMenu(!showVoiceMenu)}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-semibold transition-all ${
              showVoiceMenu ? 'bg-purple-500 text-white' : 'text-gray-400 hover:bg-gray-100'
            }`}
            title="Choisir la voix"
            data-testid="voice-selector-btn"
          >
            <Mic2 className="w-3 h-3" />
            <ChevronUp className={`w-3 h-3 transition-transform ${showVoiceMenu ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Render the appropriate interface */}
      {mode === 'webrtc' ? (
        <VoiceAssistantButton
          assistantId={activeId}
          onCallStart={onCallStart}
          onCallEnd={onCallEnd}
          onTranscript={onTranscript}
          onError={(e) => {
            onError?.(e);
            const errorMsg = e?.error?.message || e?.message || '';
            if (errorMsg.includes('response body') || errorMsg.includes('audio') || errorMsg.includes('micro')) {
              setShowFallback(true);
            }
          }}
          variant="minimal-large"
          preferredLanguage={preferredLanguage}
        />
      ) : (
        <VoiceMessageButton
          onTranscript={onTranscript}
          onResponse={(text) => console.log('Assistant:', text)}
          onError={onError}
          language={preferredLanguage}
        />
      )}

      {/* Fallback suggestion popup */}
      {showFallback && mode === 'webrtc' && (
        <div className="absolute bottom-24 right-0 bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-3 w-64 animate-in fade-in slide-in-from-bottom-2">
          <p className="text-sm text-amber-800 mb-2">
            Problème avec l'appel direct ? Essayez le <strong>mode Message vocal</strong>.
          </p>
          <button
            onClick={() => { setMode('message'); setShowFallback(false); }}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm py-2 px-3 rounded-lg transition-colors"
          >
            Passer en mode Message
          </button>
          <button
            onClick={() => setShowFallback(false)}
            className="w-full text-gray-500 text-xs mt-1 hover:text-gray-700"
          >
            Fermer
          </button>
        </div>
      )}
    </div>
  );
};

export default VoiceInterfaceCombined;
